import React, { useState, useEffect } from 'react';
import { Link2, Unlink, RefreshCw, Check, AlertTriangle, Lock, ChevronDown, ChevronUp } from 'lucide-react';

const API = 'https://qbdp-backend-production.up.railway.app/api';
const token = () => sessionStorage.getItem('qbdp_token');
const hdr   = () => ({ Authorization: `Bearer ${token()}`, 'Content-Type': 'application/json' });

const card = {
  background: 'white', borderRadius: 14, border: '1px solid #e5e7eb',
  padding: '24px 28px', marginBottom: 20,
};

// ── SALTO KS ──────────────────────────────────────────────────────────────────
function SaltoSection() {
  const [status, setStatus]   = useState(null);
  const [form, setForm]       = useState({ client_id: '', client_secret: '', site_id: '' });
  const [locks, setLocks]     = useState([]);
  const [portes, setPortes]   = useState([]);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState(null);
  const [showLocks, setShowLocks] = useState(false);
  const [linkSaved, setLinkSaved] = useState({});

  useEffect(() => {
    fetch(`${API}/salto/status`, { headers: hdr() })
      .then(r => r.json()).then(setStatus).catch(() => {});
  }, []);

  const connect = async () => {
    setSaving(true); setError(null);
    try {
      const r = await fetch(`${API}/salto/connect`, {
        method: 'POST', headers: hdr(), body: JSON.stringify(form),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error);
      setStatus({ connected: true, site_id: form.site_id, connected_at: new Date().toISOString() });
    } catch (e) { setError(e.message); }
    setSaving(false);
  };

  const disconnect = async () => {
    await fetch(`${API}/salto/disconnect`, { method: 'DELETE', headers: hdr() });
    setStatus({ connected: false });
    setLocks([]); setPortes([]);
  };

  const loadLocks = async () => {
    setShowLocks(true);
    const [lr, pr] = await Promise.all([
      fetch(`${API}/salto/locks`, { headers: hdr() }).then(r => r.json()),
      fetch(`${API}/salto/portes`, { headers: hdr() }).then(r => r.json()),
    ]);
    setLocks(Array.isArray(lr) ? lr : lr.data || []);
    setPortes(Array.isArray(pr) ? pr : []);
  };

  const linkLock = async (salto_lock_id, porte_id) => {
    const r = await fetch(`${API}/salto/link`, {
      method: 'POST', headers: hdr(),
      body: JSON.stringify({ porte_id: Number(porte_id), salto_lock_id }),
    });
    if (r.ok) setLinkSaved(prev => ({ ...prev, [salto_lock_id]: true }));
    setTimeout(() => setLinkSaved(prev => ({ ...prev, [salto_lock_id]: false })), 2000);
  };

  return (
    <div style={card}>
      {/* En-tête */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 44, height: 44, borderRadius: 10, background: '#f0f9ff', border: '1px solid #bae6fd', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Lock size={20} color="#0284c7" />
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15, color: '#111827' }}>SALTO KS</div>
            <div style={{ fontSize: 12, color: '#6b7280' }}>Serrures connectées professionnelles — ouverture à distance</div>
          </div>
        </div>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 6, padding: '5px 12px', borderRadius: 20,
          background: status?.connected ? '#dcfce7' : '#f3f4f6',
          color: status?.connected ? '#166534' : '#6b7280', fontSize: 12, fontWeight: 600,
        }}>
          <div style={{ width: 7, height: 7, borderRadius: '50%', background: status?.connected ? '#4ade80' : '#d1d5db' }} />
          {status?.connected ? `Connecté — site ${status.site_id}` : 'Non connecté'}
        </div>
      </div>

      {/* Formulaire de connexion */}
      {!status?.connected && (
        <div style={{ background: '#f9fafb', borderRadius: 10, padding: 20, marginBottom: 16 }}>
          <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 16 }}>
            Nécessite un compte partenaire SALTO KS. Obtenez vos identifiants API sur{' '}
            <a href="https://developer.saltosystems.com" target="_blank" rel="noreferrer" style={{ color: '#0284c7' }}>
              developer.saltosystems.com
            </a>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div>
              <div style={{ fontSize: 12, fontWeight: 500, color: '#374151', marginBottom: 4 }}>Client ID</div>
              <input
                value={form.client_id}
                onChange={e => setForm(f => ({ ...f, client_id: e.target.value }))}
                placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                style={{ width: '100%', border: '1px solid #d1d5db', borderRadius: 8, padding: '8px 12px', fontSize: 13, outline: 'none', boxSizing: 'border-box' }}
              />
            </div>
            <div>
              <div style={{ fontSize: 12, fontWeight: 500, color: '#374151', marginBottom: 4 }}>Client Secret</div>
              <input
                type="password"
                value={form.client_secret}
                onChange={e => setForm(f => ({ ...f, client_secret: e.target.value }))}
                placeholder="••••••••••••••••"
                style={{ width: '100%', border: '1px solid #d1d5db', borderRadius: 8, padding: '8px 12px', fontSize: 13, outline: 'none', boxSizing: 'border-box' }}
              />
            </div>
          </div>
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 500, color: '#374151', marginBottom: 4 }}>Site ID SALTO</div>
            <input
              value={form.site_id}
              onChange={e => setForm(f => ({ ...f, site_id: e.target.value }))}
              placeholder="site_xxxxxxxxxx"
              style={{ width: 260, border: '1px solid #d1d5db', borderRadius: 8, padding: '8px 12px', fontSize: 13, outline: 'none' }}
            />
          </div>
          {error && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#991b1b', background: '#fee2e2', borderRadius: 8, padding: '8px 12px', marginBottom: 12, fontSize: 12 }}>
              <AlertTriangle size={13} /> {error}
            </div>
          )}
          <button onClick={connect} disabled={saving || !form.client_id || !form.client_secret || !form.site_id}
            style={{ background: saving ? '#93c5fd' : '#1a56db', color: 'white', border: 'none', borderRadius: 8, padding: '10px 20px', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
            <Link2 size={14} /> {saving ? 'Connexion…' : 'Connecter SALTO KS'}
          </button>
        </div>
      )}

      {/* Actions une fois connecté */}
      {status?.connected && (
        <div>
          <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
            <button onClick={loadLocks}
              style={{ background: '#eff6ff', color: '#1a56db', border: '1px solid #bfdbfe', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
              <RefreshCw size={13} /> Voir mes serrures SALTO
            </button>
            <button onClick={disconnect}
              style={{ background: '#fff', color: '#6b7280', border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 16px', fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
              <Unlink size={13} /> Déconnecter
            </button>
          </div>

          {/* Liste des serrures + liaison QBDP */}
          {showLocks && locks.length > 0 && (
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 10 }}>
                Associer chaque serrure SALTO à une porte QBDP
              </div>
              <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 12 }}>
                Une fois associée, l'employé peut ouvrir la serrure directement depuis l'onglet Ouvrir de l'app mobile.
              </div>
              <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
                {locks.map((lock, i) => (
                  <div key={lock.id} style={{
                    display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px',
                    borderBottom: i < locks.length - 1 ? '1px solid #f3f4f6' : 'none',
                    background: 'white',
                  }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: 13, color: '#111827' }}>{lock.name || lock.id}</div>
                      <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>ID : {lock.id}</div>
                    </div>
                    <select
                      defaultValue={portes.find(p => p.salto_lock_id === lock.id)?.id || ''}
                      onChange={e => e.target.value && linkLock(lock.id, e.target.value)}
                      style={{ border: '1px solid #d1d5db', borderRadius: 8, padding: '6px 10px', fontSize: 12, color: '#374151', outline: 'none', background: 'white', minWidth: 200 }}
                    >
                      <option value="">— Aucune porte QBDP —</option>
                      {portes.map(p => (
                        <option key={p.id} value={p.id}>{p.batiment_nom} › {p.nom}</option>
                      ))}
                    </select>
                    {linkSaved[lock.id] && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#166534', fontSize: 12 }}>
                        <Check size={13} /> Lié
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          {showLocks && locks.length === 0 && (
            <div style={{ fontSize: 13, color: '#6b7280', background: '#f9fafb', borderRadius: 8, padding: 16 }}>
              Aucune serrure trouvée sur ce site SALTO.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── NUKI ──────────────────────────────────────────────────────────────────────
function NukiSection() {
  const [status, setStatus]       = useState(null);
  const [apiToken, setApiToken]   = useState('');
  const [locks, setLocks]         = useState([]);
  const [portes, setPortes]       = useState([]);
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState(null);
  const [showLocks, setShowLocks] = useState(false);
  const [linkSaved, setLinkSaved] = useState({});

  useEffect(() => {
    fetch(`${API}/nuki/status`, { headers: hdr() })
      .then(r => r.json()).then(setStatus).catch(() => {});
  }, []);

  const connect = async () => {
    setSaving(true); setError(null);
    try {
      const r = await fetch(`${API}/nuki/connect`, {
        method: 'POST', headers: hdr(), body: JSON.stringify({ api_token: apiToken }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error);
      setStatus({ connected: true, connected_at: new Date().toISOString() });
    } catch (e) { setError(e.message); }
    setSaving(false);
  };

  const disconnect = async () => {
    await fetch(`${API}/nuki/disconnect`, { method: 'DELETE', headers: hdr() });
    setStatus({ connected: false });
    setLocks([]); setPortes([]);
  };

  const loadLocks = async () => {
    setShowLocks(true);
    const [lr, pr] = await Promise.all([
      fetch(`${API}/nuki/locks`, { headers: hdr() }).then(r => r.json()),
      fetch(`${API}/nuki/portes`, { headers: hdr() }).then(r => r.json()),
    ]);
    setLocks(Array.isArray(lr) ? lr : []);
    setPortes(Array.isArray(pr) ? pr : []);
  };

  const linkLock = async (nuki_smartlock_id, porte_id) => {
    await fetch(`${API}/nuki/link`, {
      method: 'POST', headers: hdr(),
      body: JSON.stringify({ porte_id: Number(porte_id), nuki_smartlock_id }),
    });
    setLinkSaved(prev => ({ ...prev, [nuki_smartlock_id]: true }));
    setTimeout(() => setLinkSaved(prev => ({ ...prev, [nuki_smartlock_id]: false })), 2000);
  };

  return (
    <div style={card}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 44, height: 44, borderRadius: 10, background: '#fff7ed', border: '1px solid #fed7aa', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Lock size={20} color="#ea580c" />
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15, color: '#111827' }}>Nuki</div>
            <div style={{ fontSize: 12, color: '#6b7280' }}>Serrures connectées — accès immédiat sans partenariat</div>
          </div>
        </div>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 6, padding: '5px 12px', borderRadius: 20,
          background: status?.connected ? '#dcfce7' : '#f3f4f6',
          color: status?.connected ? '#166534' : '#6b7280', fontSize: 12, fontWeight: 600,
        }}>
          <div style={{ width: 7, height: 7, borderRadius: '50%', background: status?.connected ? '#4ade80' : '#d1d5db' }} />
          {status?.connected ? 'Connecté' : 'Non connecté'}
        </div>
      </div>

      {!status?.connected && (
        <div style={{ background: '#f9fafb', borderRadius: 10, padding: 20, marginBottom: 16 }}>
          <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>
            1. Va sur <a href="https://web.nuki.io" target="_blank" rel="noreferrer" style={{ color: '#ea580c' }}>web.nuki.io</a> → Compte → API → Générer un token
          </div>
          <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 16 }}>
            2. Copie le token et colle-le ici — c'est immédiat, aucune validation requise.
          </div>
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 12, fontWeight: 500, color: '#374151', marginBottom: 4 }}>Token API Nuki</div>
            <input
              type="password"
              value={apiToken}
              onChange={e => setApiToken(e.target.value)}
              placeholder="Colle ton token Nuki ici"
              style={{ width: 360, border: '1px solid #d1d5db', borderRadius: 8, padding: '8px 12px', fontSize: 13, outline: 'none' }}
            />
          </div>
          {error && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#991b1b', background: '#fee2e2', borderRadius: 8, padding: '8px 12px', marginBottom: 12, fontSize: 12 }}>
              <AlertTriangle size={13} /> {error}
            </div>
          )}
          <button onClick={connect} disabled={saving || !apiToken}
            style={{ background: saving ? '#fdba74' : '#ea580c', color: 'white', border: 'none', borderRadius: 8, padding: '10px 20px', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
            <Link2 size={14} /> {saving ? 'Connexion…' : 'Connecter Nuki'}
          </button>
        </div>
      )}

      {status?.connected && (
        <div>
          <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
            <button onClick={loadLocks}
              style={{ background: '#fff7ed', color: '#ea580c', border: '1px solid #fed7aa', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
              <RefreshCw size={13} /> Voir mes serrures Nuki
            </button>
            <button onClick={disconnect}
              style={{ background: '#fff', color: '#6b7280', border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 16px', fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
              <Unlink size={13} /> Déconnecter
            </button>
          </div>

          {showLocks && locks.length > 0 && (
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 10 }}>
                Associer chaque serrure Nuki à une porte QBDP
              </div>
              <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
                {locks.map((lock, i) => (
                  <div key={lock.smartlockId} style={{
                    display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px',
                    borderBottom: i < locks.length - 1 ? '1px solid #f3f4f6' : 'none',
                    background: 'white',
                  }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: 13, color: '#111827' }}>{lock.name}</div>
                      <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>ID : {lock.smartlockId}</div>
                    </div>
                    <select
                      defaultValue={portes.find(p => p.nuki_smartlock_id === String(lock.smartlockId))?.id || ''}
                      onChange={e => e.target.value && linkLock(String(lock.smartlockId), e.target.value)}
                      style={{ border: '1px solid #d1d5db', borderRadius: 8, padding: '6px 10px', fontSize: 12, color: '#374151', outline: 'none', background: 'white', minWidth: 200 }}
                    >
                      <option value="">— Aucune porte QBDP —</option>
                      {portes.map(p => (
                        <option key={p.id} value={p.id}>{p.batiment_nom} › {p.nom}</option>
                      ))}
                    </select>
                    {linkSaved[String(lock.smartlockId)] && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#166534', fontSize: 12 }}>
                        <Check size={13} /> Lié
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          {showLocks && locks.length === 0 && (
            <div style={{ fontSize: 13, color: '#6b7280', background: '#f9fafb', borderRadius: 8, padding: 16 }}>
              Aucune serrure trouvée sur ce compte Nuki.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── KNX ───────────────────────────────────────────────────────────────────────
function KnxSection() {
  const [status,  setStatus]  = useState(null);
  const [form,    setForm]    = useState({ gateway_ip: '', gateway_port: '3671' });
  const [saving,  setSaving]  = useState(false);
  const [testing, setTesting] = useState(false);
  const [error,   setError]   = useState(null);
  const [ok,      setOk]      = useState(false);

  useEffect(() => {
    fetch(`${API}/knx/status`, { headers: hdr() })
      .then(r => r.json())
      .then(d => { setStatus(d); if (d.gateway_ip) setForm({ gateway_ip: d.gateway_ip, gateway_port: String(d.gateway_port || 3671) }); })
      .catch(() => {});
  }, []);

  const connect = async () => {
    setSaving(true); setError(null); setOk(false);
    try {
      const r = await fetch(`${API}/knx/connect`, {
        method: 'POST', headers: hdr(),
        body: JSON.stringify({ gateway_ip: form.gateway_ip, gateway_port: Number(form.gateway_port) }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error);
      setStatus({ connected: true, gateway_ip: form.gateway_ip, gateway_port: Number(form.gateway_port) });
      setOk(true); setTimeout(() => setOk(false), 3000);
    } catch (e) { setError(e.message); }
    setSaving(false);
  };

  const disconnect = async () => {
    await fetch(`${API}/knx/disconnect`, { method: 'DELETE', headers: hdr() });
    setStatus({ connected: false }); setOk(false);
  };

  const test = async () => {
    setTesting(true); setError(null);
    try {
      const r = await fetch(`${API}/knx/send`, {
        method: 'POST', headers: hdr(),
        body: JSON.stringify({ group_address: '0/0/0', value: 0 }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error);
      setOk(true); setTimeout(() => setOk(false), 3000);
    } catch (e) { setError(e.message); }
    setTesting(false);
  };

  const inp = { width: '100%', border: '1px solid #e5e7eb', borderRadius: 8, padding: '9px 12px', fontSize: 13, outline: 'none', marginTop: 6, boxSizing: 'border-box' };

  return (
    <div style={card}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 40, height: 40, borderRadius: 10, background: '#fdf4ff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>🔌</div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15, color: '#111827' }}>KNX / KNXnet·IP</div>
            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>Bâtiments tertiaires — portes, volets, lumières, thermostats</div>
          </div>
        </div>
        {status?.connected && (
          <span style={{ background: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0', borderRadius: 20, padding: '3px 12px', fontSize: 12, fontWeight: 600 }}>● Connecté</span>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 10, marginBottom: 10 }}>
        <div>
          <div style={{ fontSize: 12, fontWeight: 500, color: '#374151' }}>IP de la passerelle KNX</div>
          <input style={inp} value={form.gateway_ip} placeholder="192.168.1.x" onChange={e => setForm(f => ({ ...f, gateway_ip: e.target.value }))} />
        </div>
        <div style={{ width: 90 }}>
          <div style={{ fontSize: 12, fontWeight: 500, color: '#374151' }}>Port UDP</div>
          <input style={inp} value={form.gateway_port} placeholder="3671" onChange={e => setForm(f => ({ ...f, gateway_port: e.target.value }))} />
        </div>
      </div>

      <div style={{ background: '#faf5ff', border: '1px solid #e9d5ff', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: '#7e22ce', marginBottom: 14 }}>
        💡 La passerelle KNX doit être accessible depuis internet (port UDP 3671 ouvert ou IP publique fixe). Elle sert à la fois pour l'ouverture de portes (onglet Ouvrir) et la domotique maison.
      </div>

      {error && <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: '#dc2626', marginBottom: 10 }}>{error}</div>}
      {ok    && <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: '#16a34a', marginBottom: 10 }}>✓ Passerelle KNX configurée avec succès</div>}

      <div style={{ display: 'flex', gap: 10 }}>
        <button onClick={connect} disabled={saving || !form.gateway_ip}
          style={{ flex: 1, padding: '9px 0', borderRadius: 8, border: 'none', background: '#7c3aed', color: 'white', fontWeight: 600, fontSize: 13, cursor: 'pointer', opacity: saving || !form.gateway_ip ? 0.5 : 1 }}>
          {saving ? 'Connexion…' : status?.connected ? 'Mettre à jour' : 'Connecter'}
        </button>
        {status?.connected && (
          <>
            <button onClick={test} disabled={testing}
              style={{ padding: '9px 16px', borderRadius: 8, border: '1px solid #e9d5ff', background: 'white', color: '#7c3aed', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
              {testing ? 'Test…' : 'Tester'}
            </button>
            <button onClick={disconnect}
              style={{ padding: '9px 16px', borderRadius: 8, border: '1px solid #e5e7eb', background: 'white', color: '#6b7280', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
              Déconnecter
            </button>
          </>
        )}
      </div>

      {status?.connected && (
        <div style={{ marginTop: 16, borderTop: '1px solid #f3f4f6', paddingTop: 14, fontSize: 12, color: '#6b7280' }}>
          Pour associer une adresse KNX à une porte, allez dans <strong>Accès → modifier une porte</strong> et renseignez l'adresse de groupe (ex : <code>1/0/1</code>).
        </div>
      )}
    </div>
  );
}

// ── PAGE PRINCIPALE ───────────────────────────────────────────────────────────
export default function Integrations() {
  return (
    <div style={{ padding: '32px 32px 64px', maxWidth: 820 }}>
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: '#111827', margin: 0 }}>Intégrations</h1>
        <p style={{ fontSize: 13, color: '#6b7280', marginTop: 6 }}>
          Connectez vos systèmes de contrôle d'accès existants à l'application QBDP.
        </p>
      </div>
      <NukiSection />
      <SaltoSection />
      <KnxSection />
    </div>
  );
}
